import React, { useState, useEffect, useRef } from 'react'
import { Card, Form, Input, Button, Typography, Space, Divider, App, Checkbox, Alert, Tabs, Menu } from 'antd'
import { UserOutlined, LockOutlined, MailOutlined, VideoCameraOutlined, GiftOutlined, MobileOutlined, HomeOutlined, PictureOutlined, HistoryOutlined } from '@ant-design/icons'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuthStore } from '@/store/authStore'
import { apiClient } from '@/services/api'
import { authService } from '@/services/authService'
import { useSiteConfigStore } from '@/store/siteConfigStore'
import { couponService, CouponInfo } from '@/services/couponService'

const { Title, Paragraph, Text } = Typography

const RegisterPage: React.FC = () => {
  const { message } = App.useApp()
  const [form] = Form.useForm()
  const [phoneForm] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [smsLoading, setSmsLoading] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [bonusInfo, setBonusInfo] = useState<any>(null)
  const [couponCode, setCouponCode] = useState('')
  const [couponInfo, setCouponInfo] = useState<CouponInfo | null>(null)
  const [verifyingCoupon, setVerifyingCoupon] = useState(false)
  const countdownTimerRef = useRef<number | null>(null)
  const { register, phoneRegister } = useAuthStore()
  const navigate = useNavigate()
  const { config: siteConfig } = useSiteConfigStore()
  const [searchParams] = useSearchParams()
  const referralCodeFromUrl = searchParams.get('ref') || ''

  // 清理计时器
  useEffect(() => {
    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current)
      }
    }
  }, [])

  // 获取注册赠送积分配置
  useEffect(() => {
    const fetchBonusInfo = async () => {
      try {
        const response = await apiClient.get('/config/register_bonus')
        if (response.data?.success) {
          setBonusInfo(response.data.data)
        }
      } catch (error: any) {
        console.error('获取注册赠送配置失败:', error)
        // 使用默认值，不阻止用户注册
        setBonusInfo({
          enabled: true,
          bonusPoints: 10,
          description: '新用户注册赠送10积分',
          validUntil: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
        })
      }
    }
    
    fetchBonusInfo()
  }, [])

  // 验证优惠券
  const handleVerifyCoupon = async () => {
    if (!couponCode.trim()) {
      message.warning('请输入优惠券代码')
      return
    }

    setVerifyingCoupon(true)
    try {
      const info = await couponService.verifyCoupon(couponCode.trim())
      setCouponInfo(info)
      message.success(`优惠券有效！将获得 ${info.credits} 积分`)
    } catch (error: any) {
      setCouponInfo(null)
      message.error(error.response?.data?.message || error.message || '优惠券验证失败')
    } finally {
      setVerifyingCoupon(false)
    }
  }

  const handleRegister = async (values: any) => {
    setLoading(true)
    try {
      // 如果有优惠券代码或推荐码，添加到注册数据中
      const registerData: any = { ...values }
      if (couponCode.trim()) {
        registerData.couponCode = couponCode.trim()
      }
      if (referralCodeFromUrl) {
        registerData.referralCode = referralCodeFromUrl
      }
      
      await register(registerData)
      message.success('注册成功，欢迎加入Sora2 AI Platform！')
      navigate('/')
    } catch (error: any) {
      console.error('[RegisterPage] 注册失败:', error);
      message.error(error.response?.data?.message || '注册失败')
    } finally {
      setLoading(false)
    }
  }

  // 发送短信验证码
  const handleSendSms = async () => {
    const phone = phoneForm.getFieldValue('phone')
    if (!phone) {
      message.error('请先输入手机号')
      return
    }

    if (!/^1[3-9]\d{9}$/.test(phone)) {
      message.error('请输入有效的手机号')
      return
    }

    setSmsLoading(true)
    try {
      const response = await authService.sendSmsCode(phone, 'REGISTER')
      message.success('验证码已发送')
      
      // 开发环境显示验证码
      if (response.data?.code) {
        console.log('验证码（开发环境）:', response.data.code)
      }
      
      // 清理之前的计时器
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current)
      }
      
      setCountdown(60)
      countdownTimerRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            if (countdownTimerRef.current) {
              clearInterval(countdownTimerRef.current)
            }
            return 0
          }
          return prev - 1
        })
      }, 1000)
    } catch (error: any) {
      message.error(error.response?.data?.message || '发送验证码失败')
    } finally {
      setSmsLoading(false)
    }
  }

  // 手机注册
  const handlePhoneRegister = async (values: any) => {
    setLoading(true)
    try {
      // 如果有优惠券代码或推荐码，添加到注册数据中
      const registerData: any = { ...values }
      if (couponCode.trim()) {
        registerData.couponCode = couponCode.trim()
      }
      if (referralCodeFromUrl) {
        registerData.referralCode = referralCodeFromUrl
      }
      await phoneRegister(registerData.phone, registerData.code, registerData.username, registerData.couponCode, registerData.referralCode)
      message.success('注册成功，欢迎加入Sora2 AI Platform！')
      navigate('/')
    } catch (error: any) {
      message.error(error.response?.data?.message || '注册失败')
    } finally {
      setLoading(false)
    }
  }

  // 导航菜单项
  const menuItems = [
    {
      key: '/',
      icon: <HomeOutlined />,
      label: <Link to="/" className="navbar-center" style={{ fontSize: '14px', color: '#555', textDecoration: 'none', padding: '6px 10px', borderRadius: '4px', transition: 'background 0.2s' }}>首页</Link>,
    },
    {
      key: '/video',
      icon: <VideoCameraOutlined />,
      label: <Link to="/video" className="navbar-center" style={{ fontSize: '14px', color: '#555', textDecoration: 'none', padding: '6px 10px', borderRadius: '4px', transition: 'background 0.2s' }}>视频生成</Link>,
    },
    {
      key: '/image',
      icon: <PictureOutlined />,
      label: <Link to="/image" className="navbar-center" style={{ fontSize: '14px', color: '#555', textDecoration: 'none', padding: '6px 10px', borderRadius: '4px', transition: 'background 0.2s' }}>图像生成</Link>,
    },
    {
      key: '/history',
      icon: <HistoryOutlined />,
      label: <Link to="/history" className="navbar-center" style={{ fontSize: '14px', color: '#555', textDecoration: 'none', padding: '6px 10px', borderRadius: '4px', transition: 'background 0.2s' }}>历史记录</Link>,
    },
  ]

  return (
    <div className="min-h-screen flex flex-col register-page">
      {/* 顶部导航栏 */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50" style={{ height: '56px', fontFamily: 'Segoe UI, sans-serif' }}>
        <div className="container-max flex items-center justify-between max-w-7xl mx-auto h-full">
          {/* Logo */}
          <motion.div 
            className="flex items-center"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            style={{ gap: '12px' }}
          >
            <Link to="/" className="navbar-left flex items-center transition-colors duration-200" style={{ gap: '12px', fontWeight: '600', fontSize: '15px', color: '#333', textDecoration: 'none' }}>
              {siteConfig?.siteLogo ? (
                <img 
                  src={siteConfig.siteLogo} 
                  alt={siteConfig.siteName || 'Logo'} 
                  className="h-8 object-contain"
                />
              ) : (
                <>
                  <div className="bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center" style={{
                    width: 'var(--spacing-xl)',
                    height: 'var(--spacing-xl)',
                    borderRadius: 'var(--radius-lg)'
                  }}>
                    <VideoCameraOutlined className="text-white text-lg" />
                  </div>
                  <span className="font-semibold hidden sm:block">
                    {siteConfig?.siteName || 'Sora2 AI'}
                  </span>
                </>
              )}
            </Link>
          </motion.div>

          {/* 导航菜单 */}
          <div className="hidden md:flex items-center" style={{ gap: '20px' }}>
            <Menu
              mode="horizontal"
              selectedKeys={[]}
              items={menuItems}
              className="border-none bg-transparent"
              style={{
                backgroundColor: 'transparent',
                border: 'none'
              }}
            />
          </div>

          {/* 右侧按钮 */}
          <div className="flex items-center" style={{ gap: '12px' }}>
            <Button 
              type="text" 
              onClick={() => navigate('/login')}
              className="text-gray-600 hover:text-blue-500 hover:bg-gray-100 rounded transition-all duration-200"
              style={{ padding: '6px 10px', fontSize: '14px' }}
            >
              登录
            </Button>
            <Button 
              type="primary" 
              onClick={() => navigate('/register')}
              className="bg-blue-600 border-none rounded transition-all duration-200"
              style={{ padding: '6px 12px', fontSize: '14px' }}
            >
              注册
            </Button>
          </div>
        </div>
      </div>

      {/* 注册表单区域 */}
      <div className="flex-1 bg-gradient-to-br from-purple-50/60 via-pink-50/40 to-blue-50/60 flex items-center justify-center pt-4 pb-0 md:py-12 px-2 md:px-4 relative overflow-hidden">
      {/* 背景装饰 */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-100/20 via-pink-100/10 to-blue-100/20"></div>
      <div className="absolute top-1/3 left-1/3 w-64 h-64 bg-purple-200/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-1/3 right-1/3 w-64 h-64 bg-pink-200/20 rounded-full blur-3xl"></div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10 md:my-0"
        style={{ marginTop: 'auto', marginBottom: '0' }}
      >
        {/* Logo和标题 */}
          <div className="text-center" style={{ marginBottom: 'var(--spacing-xl)' }}>
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
              className="inline-flex items-center justify-center bg-gradient-to-r from-blue-500 to-purple-600"
              style={{
                width: 'var(--spacing-xl)',
                height: 'var(--spacing-xl)',
                borderRadius: 'var(--radius-lg)',
                marginBottom: 'var(--spacing-md)'
              }}
          >
              <VideoCameraOutlined className="text-white text-lg" />
          </motion.div>
            <Title level={3} className="unified-title-h3" style={{ 
              marginBottom: 'var(--spacing-sm)',
              color: '#333'
            }}>
              加入 Sora2 AI
          </Title>
            <Paragraph className="text-gray-500" style={{
              fontSize: 'var(--font-small)',
              marginBottom: 0
            }}>
            创建账户，开启AI创作之旅
          </Paragraph>
        </div>

        {/* 注册表单 */}
        <Card className="unified-card">
          {/* 注册赠送积分提示 */}
          {bonusInfo && bonusInfo.enabled && (
            <Alert
              message={`🎉 新用户注册即送 ${bonusInfo.bonusPoints} 积分！`}
              description={bonusInfo.description}
              type="success"
              showIcon
              icon={<GiftOutlined />}
              style={{ marginBottom: 'var(--spacing-lg)' }}
            />
          )}

          {/* 推荐码提示 */}
          {referralCodeFromUrl && (
            <Alert
              message={`🎁 通过推荐码注册，额外获得 30 积分奖励！`}
              description={`您正在使用推荐码：${referralCodeFromUrl}。注册成功后，您和推荐人都将获得积分奖励。`}
              type="info"
              showIcon
              style={{ marginBottom: 'var(--spacing-lg)' }}
            />
          )}
          
          <Tabs
            defaultActiveKey="phone"
            items={[
              {
                key: 'phone',
                label: '手机注册',
                children: (
                  <Form
                    form={phoneForm}
                    layout="vertical"
                    onFinish={handlePhoneRegister}
                    size="large"
                  >
                    <Form.Item
                      name="phone"
                      label="手机号"
                      rules={[
                        { required: true, message: '请输入手机号' },
                        { pattern: /^1[3-9]\d{9}$/, message: '请输入有效的手机号' }
                      ]}
                    >
                      <Input
                        prefix={<MobileOutlined />}
                        placeholder="请输入手机号"
                        className="unified-input"
                      />
                    </Form.Item>

                    <Form.Item
                      label="验证码"
                    >
                      <Space.Compact style={{ width: '100%', gap: 'var(--spacing-sm)' }}>
                        <Form.Item
                          name="code"
                          noStyle
                          rules={[
                            { required: true, message: '请输入验证码' },
                            { pattern: /^\d{6}$/, message: '请输入6位数字验证码' }
                          ]}
                        >
                          <Input
                            placeholder="请输入验证码"
                            style={{ flex: 1 }}
                            className="unified-input"
                            maxLength={6}
                          />
                        </Form.Item>
                        <Button
                          onClick={handleSendSms}
                          disabled={countdown > 0 || smsLoading}
                          loading={smsLoading}
                          className="unified-button"
                          style={{ flexShrink: 0 }}
                        >
                          {countdown > 0 ? `${countdown}s` : '获取验证码'}
                        </Button>
                      </Space.Compact>
                    </Form.Item>

                    {/* 优惠券输入 */}
                    <div className="bg-gray-50 border border-gray-200" style={{
                      marginBottom: 'var(--spacing-lg)',
                      padding: 'var(--spacing-md)',
                      borderRadius: 'var(--radius-lg)'
                    }}>
                      <Text className="text-gray-600 block" style={{
                        fontSize: 'var(--font-small)',
                        marginBottom: 'var(--spacing-sm)'
                      }}>优惠券代码（可选）</Text>
                      <Space.Compact style={{ width: '100%', gap: 'var(--spacing-sm)' }}>
                        <Input
                          placeholder="请输入优惠券代码"
                          value={couponCode}
                          onChange={(e) => {
                            setCouponCode(e.target.value.toUpperCase())
                            if (couponInfo) setCouponInfo(null)
                          }}
                          style={{ flex: 1 }}
                          className="unified-input"
                          onPressEnter={handleVerifyCoupon}
                        />
                        <Button
                          onClick={handleVerifyCoupon}
                          loading={verifyingCoupon}
                          className="unified-button"
                          style={{ flexShrink: 0 }}
                        >
                          验证
                        </Button>
                      </Space.Compact>
                      {couponInfo && (
                        <Alert
                          message={`优惠券有效！将获得 ${couponInfo.credits} 积分`}
                          description={couponInfo.description || couponInfo.name}
                          type="success"
                          showIcon
                          className="mt-2"
                          closable
                          onClose={() => setCouponInfo(null)}
                        />
                      )}
                    </div>

                    <Form.Item
                      name="agreement"
                      valuePropName="checked"
                      rules={[
                        { 
                          validator: (_, value) => 
                            value ? Promise.resolve() : Promise.reject(new Error('请同意服务条款'))
                        }
                      ]}
                    >
                      <Checkbox>
                        我已阅读并同意{' '}
                        <Link to="/terms" className="text-blue-500 hover:text-blue-600">
                          服务条款
                        </Link>
                        {' '}和{' '}
                        <Link to="/privacy" className="text-blue-500 hover:text-blue-600">
                          隐私政策
                        </Link>
                      </Checkbox>
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 'var(--spacing-xl)' }}>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        block
                        className="unified-button"
                        style={{ height: '48px', fontSize: '16px' }}
                      >
                        注册账户
                      </Button>
                    </Form.Item>

                    <Divider>或</Divider>

                    <div className="text-center">
                      <Text className="text-gray-600">
                        已有账户？{' '}
                        <Link to="/login" className="text-blue-500 hover:text-blue-600 font-medium">
                          立即登录
                        </Link>
                      </Text>
                    </div>
                  </Form>
                )
              },
              {
                key: 'email',
                label: '邮箱注册',
                children: (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleRegister}
            size="large"
          >
            <Form.Item
              name="email"
              label="邮箱"
              rules={[
                { required: true, message: '请输入邮箱' },
                { type: 'email', message: '请输入有效的邮箱地址' }
              ]}
            >
              <Input
                prefix={<MailOutlined />}
                placeholder="请输入邮箱"
                className="unified-input"
              />
            </Form.Item>

            <Form.Item
              name="username"
              label="用户名"
              rules={[
                { required: true, message: '请输入用户名' },
                { min: 3, message: '用户名至少3个字符' },
                { max: 30, message: '用户名最多30个字符' }
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="请输入用户名"
                className="unified-input"
              />
            </Form.Item>

            <Form.Item
              name="password"
              label="密码"
              rules={[
                { required: true, message: '请输入密码' },
                        { min: 6, message: '密码至少6个字符' },
                { 
                          pattern: /^(?=.*[A-Za-z])(?=.*\d)/,
                          message: '密码必须包含至少一个字母和一个数字'
                }
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="请输入密码"
                className="unified-input"
              />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label="确认密码"
              dependencies={['password']}
              rules={[
                { required: true, message: '请确认密码' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve()
                    }
                    return Promise.reject(new Error('两次输入的密码不一致'))
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="请再次输入密码"
                className="unified-input"
              />
            </Form.Item>

            {/* 优惠券输入 */}
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200" style={{ marginBottom: 'var(--spacing-lg)' }}>
              <Text className="text-sm text-gray-600 block" style={{ marginBottom: 'var(--spacing-sm)' }}>优惠券代码（可选）</Text>
              <Space.Compact style={{ width: '100%', gap: 'var(--spacing-sm)' }}>
                <Input
                  placeholder="请输入优惠券代码"
                  value={couponCode}
                  onChange={(e) => {
                    setCouponCode(e.target.value.toUpperCase())
                    if (couponInfo) setCouponInfo(null)
                  }}
                  style={{ flex: 1 }}
                  className="unified-input"
                  onPressEnter={handleVerifyCoupon}
                />
                <Button
                  onClick={handleVerifyCoupon}
                  loading={verifyingCoupon}
                  className="unified-button"
                  style={{ flexShrink: 0 }}
                >
                  验证
                </Button>
              </Space.Compact>
              {couponInfo && (
                <Alert
                  message={`优惠券有效！将获得 ${couponInfo.credits} 积分`}
                  description={couponInfo.description || couponInfo.name}
                  type="success"
                  showIcon
                  className="mt-2"
                  closable
                  onClose={() => setCouponInfo(null)}
                />
              )}
            </div>

            <Form.Item
              name="agreement"
              valuePropName="checked"
              rules={[
                { 
                  validator: (_, value) => 
                    value ? Promise.resolve() : Promise.reject(new Error('请同意服务条款'))
                }
              ]}
            >
              <Checkbox>
                我已阅读并同意{' '}
                <Link to="/terms" className="text-blue-500 hover:text-blue-600">
                  服务条款
                </Link>
                {' '}和{' '}
                <Link to="/privacy" className="text-blue-500 hover:text-blue-600">
                  隐私政策
                </Link>
              </Checkbox>
            </Form.Item>

            <Form.Item style={{ marginBottom: 'var(--spacing-xl)' }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                className="unified-button"
                style={{ height: '48px', fontSize: '16px' }}
              >
                注册账户
              </Button>
            </Form.Item>

            <Divider>或</Divider>

            <div className="text-center">
              <Text className="text-gray-600">
                已有账户？{' '}
                <Link to="/login" className="text-blue-500 hover:text-blue-600 font-medium">
                  立即登录
                </Link>
              </Text>
            </div>
          </Form>
                )
              }
            ]}
          />
        </Card>

        {/* 注册福利 */}
          {bonusInfo && bonusInfo.enabled && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          style={{ marginTop: 'var(--spacing-xl)' }}
        >
          <Card className="glass-card">
            <div className="text-center">
              <Title level={4} className="unified-title-h4 text-green-600" style={{ marginBottom: 'var(--spacing-sm)' }}>
                    🎉 注册即送{bonusInfo.bonusPoints}积分
              </Title>
              <Paragraph className="text-gray-600 text-sm mb-0">
                    新用户注册即可获得{bonusInfo.bonusPoints}积分，立即开始您的AI创作之旅！
              </Paragraph>
            </div>
          </Card>
        </motion.div>
          )}

        {/* 底部链接 */}
        <div className="text-center" style={{ marginTop: 'var(--spacing-xl)', marginBottom: 'var(--spacing-md)' }}>
          <Text className="text-gray-500 text-sm">
            注册即表示您同意我们的服务条款和隐私政策
          </Text>
        </div>
      </motion.div>
      </div>
    </div>
  )
}

export default RegisterPage
